import React, { useMemo } from 'react'

import { Table } from '@mantine/core'

import PaginationArea, {
  PaginationAreaProps,
} from 'src/components/Navigation/PaginationArea/PaginationArea'
import { cn } from 'src/utils'

import TableHeader from './DnTable/TableHeader'
import { useColumnResize } from './DnTable/useColumnResize'

/*
  // 🎯 Server-side pagination
  <DnTable
    data={currentPageData}
    totalCount={totalItems}
    onLoadPage={handleLoadPage}
  />

  // 🎯 Client-side pagination
  <DnTable
    data={allData}
    defaultResultsPerPage={25}
    onPageChange={(page) => trackAnalytics(page)} // Optional: for tracking
  />
*/

type ColumnDef<Row = unknown> = {
  key: string
  label?: React.ReactNode

  // Frontend sorting - does not work with server side pagination
  isSortable?: boolean
  sortKey?: string
  sortFn?: (a: Row, b: Row) => number

  headerClassName?: string
  width?: number // Now represents percentage (0-100)
  minWidth?: number // Now represents percentage (0-100)
  maxWidth?: number // Now represents percentage (0-100)
  hidden?: boolean // Whether the column should be hidden
  render?: (row: Row, rowIndex: number) => React.ReactNode
}

interface DnTableProps<Row = unknown>
  extends Omit<React.ComponentProps<typeof Table>, 'data'> {
  columns: ColumnDef<Row>[]
  data: Row[]
  headerProps?: Record<string, unknown>
  headerClassName?: string
  rowProps?: Record<string, unknown>
  rowClassName?: string
  onRowClick?: (rowIndex: number, rowData: Row) => void

  // Column resizing
  enableColumnResize?: boolean

  // Pagination (handled automatically - provide onLoadPage for server-side, omit for client-side)
  enablePagination?: boolean
  onPageChange?: (page: number) => void // Optional callback when page changes
  defaultResultsPerPage?: number
  paginationProps?: Partial<PaginationAreaProps> & {
    initialPage?: number // Initial page number (for URL-based pagination)
    onResultsPerPageChange?: (resultsPerPage: number) => void // Optional callback when results per page changes
  }
  totalCount?: number
  onLoadPage?: (pageInfo: { offset: number; limit: number }) => void

  // Sort (provide onSortChange for server-side sort)
  onSortChange?: (sortBy: string | null, direction: 'asc' | 'desc' | null) => void

  // Loading and empty states
  loading?: boolean
  loadingRows?: number
  emptyStateMessage?: React.ReactNode
  emptyStateIcon?: React.ReactNode
}

const DnTable = <Row,>({
  columns,
  data,
  headerProps = {},
  headerClassName = '',
  rowProps = {},
  rowClassName = '',
  onRowClick = (_rowIndex, _rowData) => {},

  // Column resizing
  enableColumnResize = false,

  // Pagination (handled automatically)
  enablePagination = true,
  onPageChange,
  defaultResultsPerPage = 20,
  paginationProps = {},
  totalCount,
  onLoadPage,

  // Sort
  onSortChange,

  // Loading and empty state props
  loading = false,
  loadingRows = 5,
  emptyStateMessage = 'No data available',
  emptyStateIcon,
  ...props
}: DnTableProps<Row>) => {
  // Filter out hidden columns
  const visibleColumns = columns.filter((col) => !col.hidden)
  const [internalSortBy, setInternalSortBy] = React.useState<string | null>(
    null
  )
  const [internalSortDirection, setInternalSortDirection] = React.useState<
    'asc' | 'desc' | null
  >(null)

  // Extract pagination props
  const {
    initialPage = 1,
    onResultsPerPageChange,
    ...restPaginationProps
  } = paginationProps

  // Internal pagination state (fully managed internally)
  const [internalPage, setInternalPage] = React.useState<number>(initialPage)
  const [internalResultsPerPage, setInternalResultsPerPage] =
    React.useState<number>(defaultResultsPerPage)

  // Sync internal page with initialPage prop when it changes
  React.useEffect(() => {
    setInternalPage(initialPage)
  }, [initialPage])

  // Sync internal results per page with defaultResultsPerPage prop when it changes
  React.useEffect(() => {
    setInternalResultsPerPage(defaultResultsPerPage)
  }, [defaultResultsPerPage])

  // Pagination mode: server-side if onLoadPage provided, client-side otherwise
  const isServerSidePagination = !!onLoadPage

  const activePage = internalPage
  const activeResultsPerPage = internalResultsPerPage

  // Auto-calculate totalPages based on data and pagination mode
  const calculatedTotalPages = useMemo(() => {
    if (!enablePagination || !activeResultsPerPage) return 1

    // Server-side: use totalCount, client-side: calculate from data length
    if (isServerSidePagination && totalCount !== undefined) {
      return Math.ceil(totalCount / activeResultsPerPage)
    }

    // For client-side pagination, use data length
    return Math.ceil(data.length / activeResultsPerPage)
  }, [
    enablePagination,
    activeResultsPerPage,
    data.length,
    isServerSidePagination,
    totalCount,
  ])

  const activeSortBy = internalSortBy
  const activeSortDirection = internalSortDirection

  const sortedData = useMemo(() => {
    if (!Array.isArray(data) || !activeSortBy || !activeSortDirection)
      return data
    const col = visibleColumns.find(
      (c) => (c.sortKey ?? c.key) === activeSortBy
    )
    if (!col) return data

    const baseComparator = col.sortFn
    if (!baseComparator) return data

    const cmp = (a: Row, b: Row) =>
      activeSortDirection === 'asc'
        ? baseComparator(a, b)
        : -baseComparator(a, b)

    return [...data].sort(cmp)
  }, [data, visibleColumns, activeSortBy, activeSortDirection])

  // Calculate pagination slice
  const paginatedData = useMemo(() => {
    // Server-side: data is pre-paginated, client-side: slice the data
    if (isServerSidePagination) return sortedData

    if (!enablePagination || !activeResultsPerPage) return sortedData

    const itemsPerPage = activeResultsPerPage
    const startIndex = (activePage - 1) * itemsPerPage
    const endIndex = startIndex + itemsPerPage

    return sortedData.slice(startIndex, endIndex)
  }, [
    sortedData,
    enablePagination,
    activePage,
    activeResultsPerPage,
    isServerSidePagination,
  ])

  const cells: React.ReactNode[][] = useMemo(
    () =>
      Array.isArray(paginatedData)
        ? paginatedData.map((row, rowIndex) =>
            visibleColumns.map((col) =>
              typeof col.render === 'function'
                ? col.render(row, rowIndex)
                : row && col.key in (row as Record<string, unknown>)
                ? ((row as Record<string, unknown>)[col.key] as React.ReactNode)
                : null
            )
          )
        : [],
    [paginatedData, visibleColumns]
  )

  const { columnWidths, handleStartResize } = useColumnResize<Row>({
    columns: visibleColumns,
    enableColumnResize,
  })

  // Check if any column has explicit width defined
  const hasExplicitWidths = visibleColumns.some(
    (col) => col.width !== undefined
  )

  const handlePageChange = (newPage: number) => {
    setInternalPage(newPage)
    onPageChange?.(newPage) // Notify external components of page change

    // Server-side: trigger data fetch, client-side: just update UI state
    if (isServerSidePagination && onLoadPage) {
      const offset = (newPage - 1) * activeResultsPerPage
      onLoadPage({ offset, limit: activeResultsPerPage })
    }
  }

  const handleResultsPerPageChange = (newResultsPerPage: number) => {
    setInternalResultsPerPage(newResultsPerPage)
    setInternalPage(1) // Reset to first page when changing page size
    onPageChange?.(1) // Notify external components of page reset
    onResultsPerPageChange?.(newResultsPerPage) // Notify external components of results per page change

    // Server-side: reload with new page size, client-side: just update UI
    if (isServerSidePagination && onLoadPage) {
      onLoadPage({ offset: 0, limit: newResultsPerPage })
    }
  }

  return (
    <div className="flex h-full flex-col">
      <div className="flex-1 overflow-auto">
        <Table
          highlightOnHover={!loading && cells.length > 0}
          {...props}
          style={{
            tableLayout:
              enableColumnResize || hasExplicitWidths ? 'fixed' : 'auto',
            width: '100%',
            ...props.style,
          }}
        >
          {(enableColumnResize || hasExplicitWidths) && (
            <colgroup>
              {columnWidths.map((width, index) => (
                <col key={index} style={{ width: `${width ?? 0}%` }} />
              ))}
            </colgroup>
          )}
          <Table.Thead className="sticky top-0 z-10 bg-white">
            <Table.Tr>
              {visibleColumns.map((col, index) => {
                const label = col.label ?? col.key
                const isSortable = col.isSortable ?? false
                const sortKeyStr = col.sortKey ?? col.key

                const handleSort = () => {
                  if (!isSortable) return

                  let nextSortBy: string | null = sortKeyStr
                  let nextDirection: 'asc' | 'desc' | null = 'asc'

                  if (activeSortBy === sortKeyStr) {
                    // Cycle through: asc → desc → null (original order)
                    if (activeSortDirection === 'asc') {
                      nextDirection = 'desc'
                    } else if (activeSortDirection === 'desc') {
                      nextSortBy = null
                      nextDirection = null
                    }
                  }

                  // Update internal sort state
                  setInternalSortBy(nextSortBy)
                  setInternalSortDirection(nextDirection)

                  // Notify parent for server-side sorting
                  onSortChange?.(nextSortBy, nextDirection)
                }

                const currentSortDirection =
                  activeSortBy === sortKeyStr ? activeSortDirection : null

                const columnIndex = index
                const isLastColumn = index === columns.length - 1

                return (
                  <TableHeader
                    key={col.key}
                    {...headerProps}
                    className={headerClassName}
                    isSortable={isSortable}
                    sortDirection={currentSortDirection}
                    onSort={handleSort}
                    enableResize={enableColumnResize}
                    onStartResize={(e) => handleStartResize(columnIndex, e)}
                    isLastColumn={isLastColumn}
                  >
                    {label}
                  </TableHeader>
                )
              })}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {loading ? (
              // Loading state
              Array.from({ length: loadingRows }).map((_, idx) => (
                <Table.Tr key={`loading-${idx}`} className="h-10">
                  {visibleColumns.map((_, cellIndex) => (
                    <Table.Td key={cellIndex}>
                      <div className="h-4 w-full animate-pulse rounded bg-gray-200"></div>
                    </Table.Td>
                  ))}
                </Table.Tr>
              ))
            ) : cells.length === 0 ? (
              // Empty state
              <Table.Tr>
                <Table.Td
                  colSpan={visibleColumns.length}
                  className="py-8 text-center"
                >
                  <div className="flex flex-col items-center justify-center text-gray-500">
                    {emptyStateIcon && (
                      <div className="mb-2">{emptyStateIcon}</div>
                    )}
                    <div>{emptyStateMessage}</div>
                  </div>
                </Table.Td>
              </Table.Tr>
            ) : (
              // Regular data rows
              cells.map((row, idx) => (
                <Table.Tr
                  key={idx}
                  className={cn(
                    'h-10 text-xs text-doubleNickel-gray-600 hover:bg-doubleNickel-gray-100',
                    onRowClick && 'cursor-pointer',
                    rowClassName
                  )}
                  onClick={() =>
                    onRowClick && onRowClick(idx, (paginatedData as Row[])[idx])
                  }
                  {...rowProps}
                >
                  {row.map((cell, cellIndex) => (
                    <Table.Td key={cellIndex}>{cell}</Table.Td>
                  ))}
                </Table.Tr>
              ))
            )}
          </Table.Tbody>
        </Table>
      </div>

      {enablePagination &&
        !loading &&
        cells.length > 0 &&
        (isServerSidePagination ? totalCount > 10 : data.length > 10) && (
          <PaginationArea
            page={activePage}
            totalPages={calculatedTotalPages}
            onPageChange={handlePageChange}
            resultsPerPage={activeResultsPerPage}
            setResultsPerPage={handleResultsPerPageChange}
            size="sm"
            {...restPaginationProps}
          />
        )}
    </div>
  )
}

export default DnTable
